import {gqlFetch, throwUserErrors} from './product-configurator';
import {rankProducts, planMoves, numericId, MAX_MOVES_PER_CALL, SALES_WINDOW_DAYS} from '../collection-sort.js';

const NAMESPACE = 'evp_configurator';
const AUTO_SORT_KEY = 'custom_sort';

type Query = (query: string, variables?: Record<string, unknown>) => Promise<any>;

export const SORT_ORDER_LABELS: Record<string, string> = {
  MANUAL: 'Manual', BEST_SELLING: 'Best selling', MOST_RELEVANT: 'Most relevant',
  ALPHA_ASC: 'Title A–Z', ALPHA_DESC: 'Title Z–A', PRICE_ASC: 'Price low to high', PRICE_DESC: 'Price high to low',
  CREATED: 'Oldest first', CREATED_DESC: 'Newest first',
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Full collections need dozens of sequential pages; back off instead of failing on throttling.
async function request(query: Query, text: string, variables?: Record<string, unknown>) {
  for (let attempt = 0; ; attempt++) {
    try { return await query(text, variables); }
    catch (error) {
      if (attempt >= 4 || !/throttl/i.test(error instanceof Error ? error.message : '')) throw error;
      await wait(1000 * 2 ** attempt);
    }
  }
}

async function pages(query: Query, text: string, variables: Record<string, unknown>, connection: (data: any) => any) {
  const nodes = [];
  let after: string | null = null;
  do {
    const json = await request(query, text, {...variables, after});
    const page = connection(json.data);
    if (!page) throw new Error('Collection not found.');
    nodes.push(...page.nodes);
    after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
  } while (after);
  return nodes;
}

export async function listAutoSortCollections(query: Query = gqlFetch) {
  const collections = await pages(query, `#graphql
    query CollectionSortCollections($after: String) {
      collections(first: 250, after: $after, sortKey: TITLE) {
        nodes {
          id title sortOrder
          productsCount { count }
          autoSort: metafield(namespace: "${NAMESPACE}", key: "${AUTO_SORT_KEY}") { value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `, {}, data => data.collections);
  return collections.filter((collection: any) => collection.autoSort?.value === 'true');
}

export async function currentProductIds(id: string, query: Query = gqlFetch): Promise<string[]> {
  const nodes = await pages(query, `#graphql
    query CollectionSortCurrent($id: ID!, $after: String) {
      collection(id: $id) {
        products(first: 250, after: $after, sortKey: COLLECTION_DEFAULT) {
          nodes { id }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  `, {id}, data => data.collection?.products);
  return nodes.map((node: any) => node.id);
}

// Products in BEST_SELLING order with every variant's availability, ready for rankProducts.
async function rankingInputs(id: string, query: Query) {
  const products = await pages(query, `#graphql
    query CollectionSortRanking($id: ID!, $after: String) {
      collection(id: $id) {
        products(first: 25, after: $after, sortKey: BEST_SELLING) {
          nodes {
            id title vendor tags status publishedAt
            featuredMedia { preview { image { url } } }
            variants(first: 25) { nodes { availableForSale } pageInfo { hasNextPage endCursor } }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  `, {id}, data => data.collection?.products);
  for (const product of products) {
    // Only an all-unavailable first page can change the result, so fetch the rest just for those.
    if (!product.variants.pageInfo.hasNextPage || product.variants.nodes.some((variant: any) => variant.availableForSale)) continue;
    product.variants.nodes = [...product.variants.nodes, ...await pages(query, `#graphql
      query CollectionSortVariants($id: ID!, $after: String) {
        product(id: $id) {
          variants(first: 250, after: $after) { nodes { availableForSale } pageInfo { hasNextPage endCursor } }
        }
      }
    `, {id: product.id, after: product.variants.pageInfo.endCursor}, data => data.product?.variants)];
  }
  return products;
}

const SALES_PAGE_SIZE = 1000;

// Units sold per product across the store over whole days, keyed by numeric product ID.
// Needs read_reports; callers fall back to Shopify's best-selling order when this throws.
export async function fetchUnitsSold(query: Query = gqlFetch): Promise<Map<string, number>> {
  const units = new Map<string, number>();
  for (let offset = 0; ; offset += SALES_PAGE_SIZE) {
    const json = await request(query, `#graphql
      query CollectionSortUnitsSold($shopifyql: String!) {
        shopifyqlQuery(query: $shopifyql) { tableData { rows } parseErrors }
      }
    `, {shopifyql: `FROM sales SHOW net_items_sold GROUP BY product_id SINCE startOfDay(-${SALES_WINDOW_DAYS}d) UNTIL endOfDay(-1d) ORDER BY net_items_sold DESC LIMIT ${SALES_PAGE_SIZE} OFFSET ${offset}`});
    const result = json.data.shopifyqlQuery;
    if (!result || result.parseErrors?.length) throw new Error(result?.parseErrors?.[0] || 'Sales data is unavailable.');
    const rows = result.tableData?.rows || [];
    for (const row of rows) {
      if (row.product_id) units.set(numericId(row.product_id), Number(row.net_items_sold) || 0);
    }
    if (rows.length < SALES_PAGE_SIZE) return units;
  }
}

export async function loadCollectionSort(id: string, query: Query = gqlFetch) {
  const json = await request(query, `#graphql
    query CollectionSortCollection($id: ID!) {
      collection(id: $id) {
        id title handle sortOrder
        productsCount { count }
        autoSort: metafield(namespace: "${NAMESPACE}", key: "${AUTO_SORT_KEY}") { value }
      }
    }
  `, {id});
  const collection = json.data.collection;
  if (!collection) throw new Error('Collection not found.');
  const [currentIds, products, sales] = await Promise.all([
    currentProductIds(id, query),
    rankingInputs(id, query),
    fetchUnitsSold(query).then(units => ({units, error: null}), (error: unknown) => ({units: null, error: error instanceof Error ? error.message : 'Sales data is unavailable.'})),
  ]);
  const ranked = rankProducts(products, sales.units);
  const byId = new Map(ranked.map(entry => [entry.product.id, entry]));
  if (currentIds.length !== ranked.length || currentIds.some(productId => !byId.has(productId))) {
    throw new Error('Collection products changed while loading. Reload to try again.');
  }
  return {
    collection: {...collection, autoSort: collection.autoSort?.value === 'true'},
    current: currentIds.map(productId => byId.get(productId)),
    proposed: ranked,
    salesError: sales.error,
  };
}

export async function setAutoSort(id: string, enabled: boolean, query: Query = gqlFetch) {
  const json = await query(`#graphql
    mutation CollectionSortAutoSort($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields { id value }
        userErrors { field message }
      }
    }
  `, {metafields: [{ownerId: id, namespace: NAMESPACE, key: AUTO_SORT_KEY, type: 'boolean', value: String(enabled)}]});
  throwUserErrors(json.data.metafieldsSet);
}

async function waitForJob(id: string, query: Query) {
  for (let attempt = 0; attempt < 120; attempt++) {
    const json = await request(query, `#graphql
      query CollectionSortJob($id: ID!) { job(id: $id) { id done } }
    `, {id});
    if (json.data.job?.done) return;
    await wait(1000);
  }
  throw new Error('Shopify is still reordering this collection. Reload in a few minutes to check the result.');
}

const sameOrder = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index]);

// `expectedIds` is the current order the caller previewed; applying is refused if it has changed since.
export async function applyCollectionSort({id, sortOrder, expectedIds, targetIds, onProgress = () => {}}: {
  id: string, sortOrder: string, expectedIds: string[], targetIds: string[], onProgress?: (message: string) => void,
}, query: Query = gqlFetch) {
  onProgress('Checking for changes…');
  if (!sameOrder(await currentProductIds(id, query), expectedIds)) {
    throw new Error('This collection changed since the preview loaded. Reload the preview and try again.');
  }
  let currentIds = expectedIds;
  if (sortOrder !== 'MANUAL') {
    onProgress('Switching to manual sorting…');
    const json = await query(`#graphql
      mutation CollectionSortManual($collection: CollectionUpdateInput!) {
        collectionUpdate(collection: $collection) {
          collection { id sortOrder }
          userErrors { field message }
        }
      }
    `, {collection: {id, sortOrder: 'MANUAL'}});
    throwUserErrors(json.data.collectionUpdate);
    // The starting manual order is Shopify's choice, so plan moves from what it actually is.
    currentIds = await currentProductIds(id, query);
  }
  const moves = planMoves(currentIds, targetIds);
  for (let start = 0; start < moves.length; start += MAX_MOVES_PER_CALL) {
    onProgress(`Moving products ${start + 1}–${Math.min(start + MAX_MOVES_PER_CALL, moves.length)} of ${moves.length}…`);
    const json = await request(query, `#graphql
      mutation CollectionSortReorder($id: ID!, $moves: [MoveInput!]!) {
        collectionReorderProducts(id: $id, moves: $moves) {
          job { id done }
          userErrors { field message }
        }
      }
    `, {id, moves: moves.slice(start, start + MAX_MOVES_PER_CALL)});
    throwUserErrors(json.data.collectionReorderProducts);
    const job = json.data.collectionReorderProducts.job;
    if (job && !job.done) await waitForJob(job.id, query);
  }
  onProgress('Confirming the new order…');
  if (!sameOrder(await currentProductIds(id, query), targetIds)) {
    throw new Error('Shopify finished, but the collection order does not match the preview. Reload to review it.');
  }
  return {moved: moves.length};
}
